import System from '../System';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import WorldMap from 'Prototype/World/WorldMap';
import LevelNode from 'Prototype/World/LevelNode/LevelNode';
import Game from 'Game';
import TransformComponent from 'Component/Transform';
import StateManager from 'State/StateManager';
import Animator from 'Prototype/Animator';
import RewardSyncTask from './Task/RewardSyncTask';
import { PlayMusic, SetMusicVolume, PlaySound } from '../Audio/AudioHelper';
import IBaseWorldData from './Data/IBaseWorldData';
import SagaMapHandler from './Handler/Map/SagaMapHandler';
import PIXIRenderer from '../Render/PIXIRenderer';
import { Container, Point } from 'pixi.js';
import WorldSyncTask from './Task/WorldSyncTask';
import { TaskEvents } from 'Utility/Task/TaskQueue';
import Interactable from 'Component/Interactable';
import FriendIcon from 'Prototype/World/FriendIcon';
import { getISOTime } from 'Utility/DateHelper';
import { track } from 'Utility/Tracking/Track';
import CleanupManager from 'Utility/CleanupManager';
import { TGameArea } from 'Utility/Tracking/DataSet/Transaction';
import { IGameState } from 'State/Game/IGameState';
import { queueLuckyTurnipsResources } from '../LuckyTurnips/LuckyTurnipsSystemHelper';
import { preload } from './WorldSystemHelper';
import DailyLoginTimer from '../Timer/Timers/DailyLoginTimer';
import Pet from 'Prefabs/Pet/Pet';
import { PetAnimations } from 'Utility/Helpers/Pets/PetAnimations';
import { GameActions } from 'State/Game/GameActions';
import CrossPromoMenuButton from 'Prefabs/UI/HUD/CrossPromoMenuButton';
import { LuckyWheelStateHelper } from 'State/Player/LuckyWheel/LuckyWheelStateHelper';
import LuckyWheel from 'Prefabs/LuckyWheel/LuckyWheel';
import { PopupID } from 'State/UI/PopupID';
import { UIActions } from 'State/UI/UIActions';
import WorldScene from 'Scene/WorldScene';
import PopupTask from 'Utility/Task/PopupTask';
import { PlayerActions } from 'State/Player/PlayerActions';
import { PlayerTask } from 'State/Player/PlayerTasks';
import { DEFAULT_WORLD_AUDIO_VOLUME } from 'Constants/World';
import PreWorldSyncTask from './Task/PreWorldSyncTask';
import { LanguageData } from 'Utility/LocaleManager';
import { Random } from 'Utility/Random';
import { getWorldLang } from 'Constants/AvatarsAndCrops';

const STARTUP_QUEUE_DELAY = 1000;
const QUEUE_INTERVAL = 2000;

export default class WorldSystem extends System {
    public worldNumber: number;
    public worldData: IBaseWorldData;
    public worldMap: WorldMap;
    public scene: WorldScene;

    private worldAudioClipName: string;
    private worldLoaded: boolean = false;

    /**
     * Container for 'friend' pins in order for them to appear behind the player pin always but added to the world map asynchronously.
     */
    public friendIconContainer: GameObject<PIXI.Container>;

    public levelContainer: GameObject<PIXI.Container>;
    public levelNodes: Map<number, LevelNode>;
    public animator: Animator;
    public pet!: Pet;
    public luckyWheel!: LuckyWheel;

    private worldMapTransform: TransformComponent;

    private readonly cleanupManager = new CleanupManager();
    private checkQueueIntervalRunning: boolean = false;

    constructor(state: StateManager, worldNumber: number) {
        super(state);

        this.worldNumber = worldNumber;
        this.levelNodes = new Map<number, LevelNode>();
        track('tCoinsLevelEntry', undefined);
    }

    public isXPromoIconOverlappingLevelNode(_go: CrossPromoMenuButton): boolean {
        const levelNodeContainer: Container = this.levelNodes.get(this.worldData.limits.max)?.container;

        if (!levelNodeContainer) {
            return false;
        }

        const a: { x: number; y: number; width: number; height: number } = levelNodeContainer;
        const b: { x: number; y: number; width: number; height: number } = {
            x: _go.container.x,
            y: _go.container.y,
            width: _go.container.width,
            height: _go.container.height,
        };

        // adjust for camera pos here
        // Sam: using `mapViewport` directly instead of accessing viewport component fixes issues with icon moving
        // after a level
        b.x += PIXIRenderer.mapViewport.x * -1;

        if (a.x >= b.x + b.width || a.y >= b.y + b.height || a.x + a.width <= b.x || a.y + a.height <= b.y) {
            return false;
        } else {
            return true;
        }
    }

    public init(): void {
        this.taskQueue.on(TaskEvents.START, this.tasksStarted.bind(this));
        this.taskQueue.on(TaskEvents.COMPLETE, this.tasksEnded.bind(this));
    }

    /**
     * Preloads assets required for world
     */
    public async preload(): Promise<any> {
        const preloadResult = await preload(this.worldNumber);
        this.worldData = preloadResult.worldData;
        this.worldAudioClipName = preloadResult.worldAudioClip;
        // Add in world specific locale
        const { key: worldLocaleKey } = getWorldLang(this.worldNumber);
        const locale = Game.resource.get<LanguageData>(worldLocaleKey) ?? {};
        Game.locale.setLanguagePartialData(locale);
    }
    public onUpdate(): void {
        const nodePadding = [70, 90, 80, 90];
        this.levelNodes.forEach(node => {
            const position = node.container.toGlobal(new Point());

            if (
                position.y + nodePadding[0] < 0 ||
                position.x - nodePadding[1] > PIXIRenderer.renderer.width ||
                position.y - nodePadding[2] > PIXIRenderer.renderer.height ||
                position.x + nodePadding[3] < 0
            ) {
                node.container.visible = false;
            } else {
                node.container.visible = true;
            }
        });
    }

    public onStart(): void {
        this.createWorld();

        // Preload other stuff

        // Level sprite sheets
        Game.resource.addIfNeeded('atlas-package_2', 'resource/graphics/sprite_sheets/package_2.json');

        // Lucky turnips
        const state = Game.store.getState();
        const nextLevel = Game.player.getNextLevelInWorld(this.worldNumber);
        if (nextLevel === state.playerState.starChestLevel) {
            queueLuckyTurnipsResources();
        }

        Game.resource.load();

        const queueTimeout = setTimeout(() => {
            this.worldLoaded = true;
            this.checkQueue();
        }, STARTUP_QUEUE_DELAY);
        this.cleanupManager.add(() => {
            clearTimeout(queueTimeout);
        });

        try {
            console.log(`[${getISOTime()}] WorldSystem started`);
        } catch (err) {
            /* who cares? */
        }
        track('tGameArea', TGameArea.MAP);
        track('tCardsLeft', -1);

        const dailyTimer = new DailyLoginTimer();
    }

    public onDestroy(): void {
        this.taskQueue.off(TaskEvents.START, this.tasksStarted.bind(this));
        this.taskQueue.off(TaskEvents.COMPLETE, this.tasksEnded.bind(this));
    }

    private createWorld(): void {
        this.animator = GameObject.Instantiate(Animator);
        this.scene.addGameObject(this.animator);

        const state: IGameState = Game.store.getState();

        this.worldMap = SagaMapHandler.generateWorld({
            worldData: this.worldData,
            worldNumber: this.worldNumber,
            worldLayout: this.scene.layout,
            scene: this.scene,
            rememberToCleanUp: cleanUp => {
                this.cleanupManager.add(cleanUp);
            },
        });

        this.worldMapTransform = this.worldMap.getComponent(TransformComponent);
        this.levelContainer = this.worldMap.levelContainer;
        this.friendIconContainer = this.worldMap.friendIconContainer;
        this.levelNodes = this.worldMap.levelNodes;
        this.pet = this.worldMap.petContainer.find('pet') as Pet;
        this.pet.on(GameObjectEvent.SELECTED, this.onPetClicked);
        this.cleanupManager.add(() => {
            this.pet.off(GameObjectEvent.SELECTED, this.onPetClicked);
        });

        const levelNumber = Game.player.getHighestLevelCompleted() + 1;
        this.luckyWheel = this.worldMap.luckyWheelContainer.findChildByName(`luckyWheel_${levelNumber}`) as LuckyWheel;
        if (this.luckyWheel) {
            this.luckyWheel.on(GameObjectEvent.SELECTED, this.onWheelClicked);
            this.cleanupManager.add(() => {
                this.luckyWheel.off(GameObjectEvent.SELECTED, this.onWheelClicked);
            });
        }

        this.scene.addGameObject(this.worldMap);

        // Remove the toolbox if unlocked
        if (Game.player.getHighestLevelCompleted(state) > 24) {
            const toolbox: GameObject = GameObject.findFirstByName('toolbox_map_component');
            if (toolbox) toolbox.destroy();
        }

        PlayMusic(this.worldAudioClipName, {
            volume: DEFAULT_WORLD_AUDIO_VOLUME,
        });
    }

    private readonly onPetClicked = () => {
        if (this.pet.isMoving) return;

        const state: IGameState = Game.store.getState();
        const petLevel = state.playerState.petLevel;
        const currentLevel = Game.player.getHighestLevelCompleted() + 1;
        if (petLevel === currentLevel) {
            GameActions.loadMyPetMiniGame({});
        } else {
            const clickAnimations = [PetAnimations.IDLE_CLICK, PetAnimations.IDLE_CLICK_2];
            const randomIndex = Random.int(0, clickAnimations.length - 1);
            const clickAnimation = clickAnimations[randomIndex];
            const canTransition = this.pet.canTransition(this.pet.currentAnimation, clickAnimation);

            if (canTransition) {
                this.pet.stop();
                PlaySound('pet-meow');
                this.pet.playOnce(clickAnimation).then(() => {
                    this.pet.play(PetAnimations.IDLE);
                });
            }
        }
    };

    private readonly onWheelClicked = () => {
        const playerState = Game.store.getState().playerState;
        if (LuckyWheelStateHelper.checkEligibility(playerState)) {
            UIActions.showPopup(PopupID.WHEEL, {});
        }
    };

    public cancelWheelPopupTasks(): void {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            tasks: playerState.tasks.filter(
                task => task.task !== PlayerTask.SHOW_POPUP || task.popup !== PopupID.WHEEL
            ),
        });

        this.taskQueue.forEach(task => {
            if (task instanceof PopupTask && task.getPopupID() === PopupID.WHEEL) {
                task.cancel();
            }
        });
    }

    public getCurrentLevelNode(): LevelNode {
        return this.getLevelNode(Game.player.getNextLevelInWorld(this.worldNumber));
    }

    public getPreviousLevelNode(): LevelNode {
        return this.getLevelNode(Game.player.getNextLevelInWorld(this.worldNumber) - 1);
    }

    public getLevelNode(levelNumber: number): LevelNode | undefined {
        return this.levelNodes.get(levelNumber);
    }

    public handleStateChanged(state: IGameState) {
        this.checkQueue(state);
    }

    private checkQueue(state: IGameState = Game.store.getState()) {
        if (!state.gameLoaded || (state.entryPoint?.entryPoint && !state.entryPoint.handled)) return;

        if (!this.checkQueueIntervalRunning) {
            this.checkQueueIntervalRunning = true;
            const queueInterval = Game.ticker.setInterval(this.checkQueue.bind(this), QUEUE_INTERVAL);
            this.cleanupManager.add(() => {
                Game.ticker.clearInterval(queueInterval);
            });
        }

        if (state.playerState.tasks.length === 0) return;

        if (!this.taskQueue.some(task => task instanceof RewardSyncTask))
            this.taskQueue.queueNext(new RewardSyncTask());

        if (!this.taskQueue.some(task => task instanceof PreWorldSyncTask))
            this.taskQueue.queueNext(new PreWorldSyncTask());

        if (!this.taskQueue.some(task => task instanceof WorldSyncTask)) this.taskQueue.queue(new WorldSyncTask());

        if (this.worldLoaded && state.UIState.popupID === null) {
            this.taskQueue.start(this);
        }
    }
    protected onLateUpdate(): void {}

    protected onStop(): void {
        this.cleanupManager.cleanUpAll();
    }

    protected tasksStarted(): void {
        this.scene.emit(TaskEvents.START);
        if (this.worldMap.playerIcon) {
            this.worldMap.playerIcon.getComponent(Interactable).enabled = false;
        }

        if (this.worldMap.friendIconContainer) {
            this.worldMap.friendIconContainer.children.forEach((friend: GameObject) => {
                if (friend instanceof FriendIcon) {
                    friend
                        .getComponentsInChildren(Interactable)
                        .forEach(interactable => (interactable.enabled = false));
                }
            });
        }
        this.pet.getComponent(Interactable).enabled = false;
        const nodes: Map<number, LevelNode> = this.worldMap.levelNodes || new Map<number, LevelNode>();
        nodes.forEach((levelNode: LevelNode) => {
            levelNode.getComponent(Interactable).enabled = false;
        });
    }

    protected tasksEnded(): void {
        this.scene.emit(TaskEvents.COMPLETE);

        if (this.worldMap.playerIcon) {
            this.worldMap.playerIcon.getComponent(Interactable).enabled = true;
        }
        const friendIcons: GameObject[] =
            this.worldMap.friendIconContainer && this.worldMap.friendIconContainer.children
                ? this.worldMap.friendIconContainer.children
                : [];
        friendIcons.forEach((friend: GameObject) => {
            if (friend instanceof FriendIcon) {
                friend.getComponentsInChildren(Interactable).forEach(interactable => (interactable.enabled = true));
            }
        });

        const nodes: Map<number, LevelNode> = this.worldMap.levelNodes || new Map<number, LevelNode>();
        nodes.forEach((levelNode: LevelNode) => {
            levelNode.getComponent(Interactable).enabled = true;
        });

        this.pet.getComponent(Interactable).enabled = true;
    }

    public setMusicVolume(_value: number): void {
        SetMusicVolume(this.worldAudioClipName, _value);
    }
}
